import { 
  Box, Paper, Typography, TextField, Button, Alert, 
  Container, CircularProgress
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import AuthService from '../services/AuthService';
import { log } from '../utils/errors/logger';

const ResendVerification = () => {
  const { t } = useTranslation();
  
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!email) {
      setError(t('resendVerification.validation.emailRequired', 'E-Mail-Adresse ist erforderlich'));
      return;
    }
    
    setLoading(true);
    
    try {
      await AuthService.resendVerification(email);
      setSuccess(t('resendVerification.success', 'Wenn deine E-Mail-Adresse registriert ist, wurde eine neue Verifizierungs-E-Mail gesendet.'));
      setError('');
      setEmail('');
    } catch (error: any) {
      log.error('Resend verification error', { error: error.message, email });
      setError(error.message || t('resendVerification.error', 'Fehler beim Senden der Verifizierungs-E-Mail'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography component="h1" variant="h4" color="primary" gutterBottom>
            {t('resendVerification.title', 'Verifizierungs-E-Mail erneut senden')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('resendVerification.instructions', 'Gib deine E-Mail-Adresse ein, um eine neue Verifizierungs-E-Mail zu erhalten.')}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('resendVerification.email', 'E-Mail-Adresse')}
              name="email"
              autoComplete="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading}
            >
              {loading ? 
                <CircularProgress size={24} /> : 
                t('resendVerification.submit', 'Verifizierungs-E-Mail senden')
              }
            </Button>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('resendVerification.backToLogin', 'Zurück zur')}{' '}
              <Link to="/login" style={{ textDecoration: 'none' }}>
                {t('resendVerification.login', 'Anmeldung')}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResendVerification;
